import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Building2, Stethoscope, Pill, ArrowLeft, ArrowRight, Check, 
  Plus, X, Upload, DollarSign, Globe, Mail, Phone, User, 
  FileText, MapPin, Image as ImageIcon, Link as LinkIcon,
  Facebook, Twitter, Instagram, Linkedin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { listingsAPI, specialtiesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AddListing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1 - Type
    listing_type: '',
    
    // Step 2 - Basic Info
    name: '',
    contact_name: '',
    registration_number: '',
    description: '',
    
    // Step 3 - Contact & Location
    website: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    region: '',
    
    // Step 4 - Details
    categories: [],
    specialties: [],
    services: [],
    insurance_accepted: [],
    languages: ['English'],
    price_range: '',
    
    // Step 5 - Media & Social
    logo: '',
    photos: [],
    social_networks: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
    },
    
    // Other
    booking_url: '',
    hours: {
      mon: '9am-5pm',
      tue: '9am-5pm',
      wed: '9am-5pm',
      thu: '9am-5pm',
      fri: '9am-5pm',
      sat: 'Closed',
      sun: 'Closed',
    },
  });

  const [tempInput, setTempInput] = useState({
    category: '',
    specialty: '',
    service: '',
    insurance: '',
    language: '',
    photoUrl: '',
  });

  useEffect(() => {
    loadSpecialties();
    // Check for pre-filled name from import
    const importedName = searchParams.get('name');
    if (importedName) {
      setFormData(prev => ({ ...prev, name: importedName }));
    }
  }, [searchParams]);

  const loadSpecialties = async () => {
    try {
      const res = await specialtiesAPI.getAll();
      setSpecialties(res.data);
    } catch (error) {
      console.error('Error loading specialties:', error);
    }
  };

  const listingTypes = [
    {
      id: 'facility',
      name: 'Facility',
      description: 'Hospitals, clinics, urgent care, imaging centers',
      icon: Building2,
      color: 'teal',
    },
    {
      id: 'specialist',
      name: 'Specialist',
      description: 'Doctors and medical providers',
      icon: Stethoscope,
      color: 'blue',
    },
    {
      id: 'pharmacy',
      name: 'Pharmacy',
      description: 'Local and specialty pharmacies',
      icon: Pill,
      color: 'rose',
    },
  ];

  const priceRanges = [
    { value: '$', label: '$ - Budget Friendly' },
    { value: '$$', label: '$$ - Moderate' },
    { value: '$$$', label: '$$$ - Higher End' },
    { value: '$$$$', label: '$$$$ - Premium' },
  ];

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateSocialNetwork = (network, value) => {
    setFormData((prev) => ({
      ...prev,
      social_networks: { ...prev.social_networks, [network]: value },
    }));
  };

  const addToArray = (field, value) => {
    if (value && !formData[field].includes(value)) {
      setFormData((prev) => ({ ...prev, [field]: [...prev[field], value] }));
    }
    // Reset temp input
    const inputKey = field === 'insurance_accepted' ? 'insurance' : 
                     field === 'specialties' ? 'specialty' :
                     field === 'categories' ? 'category' :
                     field === 'photos' ? 'photoUrl' : field.slice(0, -1);
    setTempInput((prev) => ({ ...prev, [inputKey]: '' }));
  };

  const removeFromArray = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: prev[field].filter((v) => v !== value) }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await listingsAPI.create(formData);
      toast.success('Listing submitted for review!');
      navigate(`/listing/${res.data.listing_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return !!formData.listing_type;
      case 2:
        return formData.name;
      case 3:
        return formData.street && formData.city && formData.state && formData.zip_code;
      case 4:
        return true; // Details are optional
      case 5:
        return true; // Media & Social are optional
      case 6:
        return true; // Preview
      default:
        return false;
    }
  };

  const stepLabels = ['Type', 'Basic Info', 'Location', 'Details', 'Media', 'Preview'];
  const totalSteps = 6;

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                What type of listing is this?
              </h2>
              <p className="text-gray-600">Choose the category that best describes your business</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {listingTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => updateField('listing_type', type.id)}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.listing_type === type.id
                      ? 'border-teal-700 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`type-${type.id}`}
                >
                  <type.icon className={`w-10 h-10 mb-4 ${
                    formData.listing_type === type.id ? 'text-teal-700' : 'text-gray-400'
                  }`} />
                  <h3 className="font-heading font-semibold text-lg text-gray-900 mb-1">{type.name}</h3>
                  <p className="text-sm text-gray-600">{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Basic Information
              </h2>
              <p className="text-gray-600">Tell us about your business</p>
            </div>

            <div className="space-y-6">
              {/* Business Name */}
              <div>
                <Label htmlFor="name" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  Business Name *
                </Label>
                <Input
                  id="name"
                  placeholder="e.g., City General Hospital"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="mt-1"
                  data-testid="listing-name-input"
                />
              </div>

              {/* Contact Name */}
              <div>
                <Label htmlFor="contact_name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  Contact Name
                </Label>
                <Input
                  id="contact_name"
                  placeholder="Primary contact person"
                  value={formData.contact_name}
                  onChange={(e) => updateField('contact_name', e.target.value)}
                  className="mt-1"
                  data-testid="listing-contact-name-input"
                />
              </div>

              {/* Company Registration Number */}
              <div>
                <Label htmlFor="registration_number" className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  Company Registration Number
                </Label>
                <Input
                  id="registration_number"
                  placeholder="Business license or registration #"
                  value={formData.registration_number}
                  onChange={(e) => updateField('registration_number', e.target.value)}
                  className="mt-1"
                  data-testid="listing-registration-input"
                />
              </div>

              {/* Brief Description */}
              <div>
                <Label htmlFor="description" className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  Brief Description
                </Label>
                <Textarea
                  id="description"
                  placeholder="Describe your services and what makes you unique..."
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={4}
                  className="mt-1"
                  data-testid="listing-description-input"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Contact & Location
              </h2>
              <p className="text-gray-600">How can patients reach you?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Website */}
              <div>
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-500" />
                  Website
                </Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  className="mt-1"
                  data-testid="listing-website-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  Contact Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="contact@yourpractice.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="mt-1"
                  data-testid="listing-email-input"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className="mt-1"
                  data-testid="listing-phone-input"
                />
              </div>

              {/* Booking URL */}
              <div>
                <Label htmlFor="booking_url" className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-gray-500" />
                  Booking URL
                </Label>
                <Input
                  id="booking_url"
                  type="url"
                  placeholder="https://booking.example.com"
                  value={formData.booking_url}
                  onChange={(e) => updateField('booking_url', e.target.value)}
                  className="mt-1"
                  data-testid="listing-booking-input"
                />
              </div>

              {/* Street Address */}
              <div className="md:col-span-2">
                <Label htmlFor="street" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Street Address *
                </Label>
                <Input
                  id="street"
                  placeholder="123 Main Street, Suite 100"
                  value={formData.street}
                  onChange={(e) => updateField('street', e.target.value)}
                  className="mt-1"
                  data-testid="listing-street-input"
                />
              </div>

              {/* City */}
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="New York"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="mt-1"
                  data-testid="listing-city-input"
                />
              </div>

              {/* State & ZIP */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    placeholder="NY"
                    value={formData.state}
                    onChange={(e) => updateField('state', e.target.value)}
                    className="mt-1"
                    data-testid="listing-state-input"
                  />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP Code *</Label>
                  <Input
                    id="zip"
                    placeholder="10001"
                    value={formData.zip_code}
                    onChange={(e) => updateField('zip_code', e.target.value)}
                    className="mt-1"
                    data-testid="listing-zip-input"
                  />
                </div>
              </div>

              {/* Region */}
              <div className="md:col-span-2">
                <Label htmlFor="region" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Region / Area
                </Label>
                <Input
                  id="region"
                  placeholder="e.g., Manhattan, Downtown, Westside"
                  value={formData.region}
                  onChange={(e) => updateField('region', e.target.value)}
                  className="mt-1"
                  data-testid="listing-region-input"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Additional Details
              </h2>
              <p className="text-gray-600">Help patients find you easier</p>
            </div>

            {/* Facility Type / Categories */}
            <div>
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                Facility Type / Categories
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="e.g., Hospital, Clinic, Medical Center"
                  value={tempInput.category}
                  onChange={(e) => setTempInput((prev) => ({ ...prev, category: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('categories', tempInput.category))}
                  data-testid="category-input"
                />
                <Button type="button" onClick={() => addToArray('categories', tempInput.category)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.categories.map((c) => (
                  <Badge key={c} variant="secondary" className="pl-3">
                    {c}
                    <button onClick={() => removeFromArray('categories', c)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Specialties */}
            <div>
              <Label>Specialties</Label>
              <div className="flex gap-2 mt-1">
                <Select
                  value={tempInput.specialty}
                  onValueChange={(v) => {
                    addToArray('specialties', v);
                  }}
                >
                  <SelectTrigger data-testid="specialty-select">
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    {specialties.map((s) => (
                      <SelectItem key={s.specialty_id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.specialties.map((s) => (
                  <Badge key={s} variant="secondary" className="pl-3">
                    {s}
                    <button onClick={() => removeFromArray('specialties', s)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Services */}
            <div>
              <Label>Services Offered</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add a service"
                  value={tempInput.service}
                  onChange={(e) => setTempInput((prev) => ({ ...prev, service: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('services', tempInput.service))}
                  data-testid="service-input"
                />
                <Button type="button" onClick={() => addToArray('services', tempInput.service)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.services.map((s) => (
                  <Badge key={s} variant="secondary" className="pl-3">
                    {s}
                    <button onClick={() => removeFromArray('services', s)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Insurance */}
            <div>
              <Label>Insurance Accepted</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add insurance"
                  value={tempInput.insurance}
                  onChange={(e) => setTempInput((prev) => ({ ...prev, insurance: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('insurance_accepted', tempInput.insurance))}
                  data-testid="insurance-input"
                />
                <Button type="button" onClick={() => addToArray('insurance_accepted', tempInput.insurance)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.insurance_accepted.map((i) => (
                  <Badge key={i} variant="secondary" className="pl-3">
                    {i}
                    <button onClick={() => removeFromArray('insurance_accepted', i)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div>
              <Label>Languages Spoken</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add language"
                  value={tempInput.language}
                  onChange={(e) => setTempInput((prev) => ({ ...prev, language: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('languages', tempInput.language))}
                  data-testid="language-input"
                />
                <Button type="button" onClick={() => addToArray('languages', tempInput.language)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.languages.map((l) => (
                  <Badge key={l} variant="secondary" className="pl-3">
                    {l}
                    <button onClick={() => removeFromArray('languages', l)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                Price Range
              </Label>
              <Select
                value={formData.price_range}
                onValueChange={(v) => updateField('price_range', v)}
              >
                <SelectTrigger className="mt-1" data-testid="price-range-select">
                  <SelectValue placeholder="Select price range" />
                </SelectTrigger>
                <SelectContent>
                  {priceRanges.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Media & Social Networks
              </h2>
              <p className="text-gray-600">Add your logo, images, and social profiles</p>
            </div>

            {/* Logo */}
            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-gray-500" />
                Logo URL
              </Label>
              <Input
                placeholder="https://yoursite.com/logo.png"
                value={formData.logo}
                onChange={(e) => updateField('logo', e.target.value)}
                className="mt-1"
                data-testid="listing-logo-input"
              />
              {formData.logo && (
                <div className="mt-2">
                  <img 
                    src={formData.logo} 
                    alt="Logo preview" 
                    className="h-16 w-16 object-contain rounded border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Gallery Images */}
            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-gray-500" />
                Gallery Images
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add image URL"
                  value={tempInput.photoUrl}
                  onChange={(e) => setTempInput((prev) => ({ ...prev, photoUrl: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('photos', tempInput.photoUrl))}
                  data-testid="photo-url-input"
                />
                <Button type="button" onClick={() => addToArray('photos', tempInput.photoUrl)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {formData.photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={photo} 
                        alt={`Gallery ${idx + 1}`}
                        className="w-full h-20 object-cover rounded border"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/100?text=Error'}
                      />
                      <button
                        onClick={() => removeFromArray('photos', photo)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Social Networks */}
            <div className="space-y-4">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-500" />
                Social Networks
              </Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Facebook */}
                <div>
                  <Label htmlFor="facebook" className="flex items-center gap-2 text-sm text-gray-600">
                    <Facebook className="w-4 h-4 text-blue-600" />
                    Facebook
                  </Label>
                  <Input
                    id="facebook"
                    placeholder="https://facebook.com/yourpage"
                    value={formData.social_networks.facebook}
                    onChange={(e) => updateSocialNetwork('facebook', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Twitter / X */}
                <div>
                  <Label htmlFor="twitter" className="flex items-center gap-2 text-sm text-gray-600">
                    <Twitter className="w-4 h-4 text-sky-500" />
                    Twitter / X
                  </Label>
                  <Input
                    id="twitter"
                    placeholder="https://twitter.com/yourhandle"
                    value={formData.social_networks.twitter}
                    onChange={(e) => updateSocialNetwork('twitter', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Instagram */}
                <div>
                  <Label htmlFor="instagram" className="flex items-center gap-2 text-sm text-gray-600">
                    <Instagram className="w-4 h-4 text-pink-600" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    placeholder="https://instagram.com/yourhandle"
                    value={formData.social_networks.instagram}
                    onChange={(e) => updateSocialNetwork('instagram', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* LinkedIn */}
                <div>
                  <Label htmlFor="linkedin" className="flex items-center gap-2 text-sm text-gray-600">
                    <Linkedin className="w-4 h-4 text-blue-700" />
                    LinkedIn
                  </Label>
                  <Input
                    id="linkedin"
                    placeholder="https://linkedin.com/company/yourcompany"
                    value={formData.social_networks.linkedin}
                    onChange={(e) => updateSocialNetwork('linkedin', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Preview Your Listing
              </h2>
              <p className="text-gray-600">Review your information before submitting</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              {/* Header with Logo */}
              <div className="flex items-start gap-4">
                {formData.logo && (
                  <img 
                    src={formData.logo} 
                    alt="Logo" 
                    className="w-16 h-16 object-contain rounded border"
                  />
                )}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={
                      formData.listing_type === 'facility' ? 'bg-teal-100 text-teal-700' :
                      formData.listing_type === 'specialist' ? 'bg-blue-100 text-blue-700' : 'bg-rose-100 text-rose-700'
                    }>
                      {formData.listing_type}
                    </Badge>
                    {formData.price_range && (
                      <Badge variant="outline">{formData.price_range}</Badge>
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-gray-900">{formData.name}</h3>
                  {formData.contact_name && (
                    <p className="text-sm text-gray-600">Contact: {formData.contact_name}</p>
                  )}
                </div>
              </div>

              {formData.description && (
                <p className="text-gray-600">{formData.description}</p>
              )}

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 font-medium">Location</p>
                  <p className="text-gray-900">{formData.street}</p>
                  <p className="text-gray-900">{formData.city}, {formData.state} {formData.zip_code}</p>
                  {formData.region && <p className="text-gray-600">Region: {formData.region}</p>}
                </div>
                <div className="space-y-1">
                  {formData.phone && (
                    <p className="text-gray-900"><Phone className="w-4 h-4 inline mr-1" />{formData.phone}</p>
                  )}
                  {formData.email && (
                    <p className="text-gray-900"><Mail className="w-4 h-4 inline mr-1" />{formData.email}</p>
                  )}
                  {formData.website && (
                    <p className="text-gray-900"><Globe className="w-4 h-4 inline mr-1" />{formData.website}</p>
                  )}
                </div>
              </div>

              {/* Categories */}
              {formData.categories.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.categories.map((c) => (
                      <Badge key={c} variant="outline">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialties */}
              {formData.specialties.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Specialties</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.specialties.map((s) => (
                      <Badge key={s} variant="secondary">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Services */}
              {formData.services.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Services</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.services.map((s) => (
                      <Badge key={s} variant="outline">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery */}
              {formData.photos.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Gallery</p>
                  <div className="grid grid-cols-4 gap-2">
                    {formData.photos.slice(0, 4).map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Gallery ${idx + 1}`} className="w-full h-16 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {Object.values(formData.social_networks).some(v => v) && (
                <div className="flex gap-3">
                  {formData.social_networks.facebook && (
                    <a href={formData.social_networks.facebook} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {formData.social_networks.twitter && (
                    <a href={formData.social_networks.twitter} target="_blank" rel="noreferrer" className="text-sky-500 hover:text-sky-700">
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {formData.social_networks.instagram && (
                    <a href={formData.social_networks.instagram} target="_blank" rel="noreferrer" className="text-pink-600 hover:text-pink-800">
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {formData.social_networks.linkedin && (
                    <a href={formData.social_networks.linkedin} target="_blank" rel="noreferrer" className="text-blue-700 hover:text-blue-900">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> Your listing will be reviewed by our team before being published.
                This usually takes 1-2 business days.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="add-listing-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {stepLabels.map((label, i) => (
              <div
                key={label}
                className={`flex items-center gap-1 ${i + 1 <= step ? 'text-teal-700' : 'text-gray-400'}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                  i + 1 < step ? 'bg-teal-700 text-white' :
                  i + 1 === step ? 'border-2 border-teal-700 text-teal-700' : 'border-2 border-gray-300'
                }`}>
                  {i + 1 < step ? <Check className="w-3 h-3" /> : i + 1}
                </div>
                <span className="hidden md:inline text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-teal-700 rounded-full transition-all"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {renderStep()}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < totalSteps ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="submit-listing-btn"
              >
                {loading ? 'Submitting...' : 'Submit Listing'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddListing;
